import { Currency } from '../../constants';
import { formatBmsmCurrency } from '../campaign/bmsm-helpers';

export interface CartMilestoneTier {
  id: string;
  available: boolean;
}

export interface CartBenefitCelebrationTier extends CartMilestoneTier {
  currentAmount: number;
}

/** What a celebration compares between renders: who is shopping and where each tier stands. */
export interface CartBenefitCelebrationSnapshot {
  identity: string;
  benefits: CartBenefitCelebrationTier[];
}

/** Banner milestone amounts drop the compact `k` suffix's case for COP only. */
export function formatCartMilestoneCurrency(currency: Currency, amount: number): string {
  const formatted = formatBmsmCurrency(currency, amount);
  return currency === Currency.Colombia ? formatted.replace(/k\b/, 'K') : formatted;
}

/** Eligibility marks progress; coupon application still controls actual reward copy. */
export function getCartMilestoneStates(tiers: CartMilestoneTier[]) {
  const currentId = tiers.filter((tier) => tier.available).at(-1)?.id;
  return tiers.map((tier) => ({ id: tier.id, reached: tier.available, current: tier.id === currentId }));
}

function hasSameCartBenefitTiers(before: CartBenefitCelebrationSnapshot, current: CartBenefitCelebrationSnapshot) {
  return (
    before.identity === current.identity &&
    before.benefits.map((benefit) => benefit.id).join('|') ===
      current.benefits.map((benefit) => benefit.id).join('|') &&
    !current.benefits.some((benefit, index) => benefit.currentAmount < before.benefits[index]?.currentAmount)
  );
}

/** A celebration survives only while the same shopper keeps the same tiers and the reward stays unlocked. */
export function canKeepCartBenefitCelebration(
  before: CartBenefitCelebrationSnapshot,
  current: CartBenefitCelebrationSnapshot,
  id: string,
) {
  return (
    hasSameCartBenefitTiers(before, current) &&
    current.benefits.find((benefit) => benefit.id === id)?.available === true
  );
}

/** The highest tier that became available since `before`, or null when nothing new unlocked. */
export function getNewlyUnlockedCartBenefit(
  before: CartBenefitCelebrationSnapshot | null,
  current: CartBenefitCelebrationSnapshot,
): CartBenefitCelebrationTier | null {
  if (!before || !hasSameCartBenefitTiers(before, current)) {
    return null;
  }
  return (
    current.benefits.filter((benefit, index) => benefit.available && !before.benefits[index]?.available).at(-1) ?? null
  );
}
